import axios from "axios";
import { recaptchaActions } from "../data/constant";
import getCsrfToken from "./helpers";
import { API_BASE_URL } from "../utils/config";

const { REACT_APP_RECAPTCHA_V3_ENABLED } = process.env;
const REQUEST_TIMEOUT_MS = 30000;

// Cache for in-flight requests to prevent duplicate API calls
let partnerSiteIdPromise = null;
let lastRequestedSiteId = null;

// Helper function to generate a fresh reCAPTCHA token
const generateRecaptchaToken = async (action) => {
  const siteKey = process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY;
  await new Promise((resolve) => {
    window.grecaptcha.ready(resolve);
  });
  return await window.grecaptcha.execute(siteKey, { action: action });
};

const getAuthorizationHeader = () => {
  const token = localStorage.getItem("consumerSite_token");
  const normalizedToken = typeof token === "string" ? token.replace(/[\r\n]/g, "").trim() : "";
  return normalizedToken ? `Bearer ${normalizedToken}` : "";
};

const getPartnerSiteHeaders = () => {
  let uid = 0;
  let siteId = 0;
  try {
    const stored = sessionStorage.getItem("partnerSiteId");
    if (stored) {
      const obj = JSON.parse(stored);
      uid = Number.parseInt(obj?.uid, 10) || 0;
      siteId = Number.parseInt(obj?.siteId, 10) || 0;
    }
  } catch (e) {
    uid = 0;
    siteId = 0;
  }
  return { "x-partner-id": uid, "x-site-id": siteId };
};

const toSafeSiteId = (value) => {
  const parsedSiteId = Number.parseInt(value, 10);
  return Number.isFinite(parsedSiteId) && parsedSiteId > 0 ? parsedSiteId : 0;
};

const encodePathSegment = (value) => encodeURIComponent(String(value ?? ""));

const toSafePayPalHeaderValue = (value) =>
  typeof value === "string" &&
  value.length <= 512 &&
  /^[A-Za-z0-9._~-]+$/.test(value)
    ? value
    : "";

// Updated login function
export const login = async (payload) => {
  let recaptchaToken = null;
  try {
    if (REACT_APP_RECAPTCHA_V3_ENABLED === "true") {
      recaptchaToken = await generateRecaptchaToken(recaptchaActions?.login);
    }

    const csrfToken = await getCsrfToken();

    const response = await axios.post(
      `${API_BASE_URL}/authenticate/token`,
      payload,
      {
        headers: {
          "X-CSRF-TOKEN": csrfToken,
          ...(recaptchaToken ? { recaptchatoken: recaptchaToken } : {}),
        },
        withCredentials: true,
        timeout: REQUEST_TIMEOUT_MS,
      }
    );
    return { data: response.data, status: response.status, API_Name: "login" };
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};
export const accountLocked = async (payload) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/send-email/v1/updateLockedAccountPassword/${encodePathSegment(payload)}`,
      undefined,
      { timeout: REQUEST_TIMEOUT_MS }
    );
    return {
      data: response.data,
      status: response.status,
      API_Name: "accountLocked",
    };
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};
export const forgotPassword = async (payload) => {
  try {
    //   const token = await getCsrfToken();
    const response = await axios.post(
      `${API_BASE_URL}/send-email/v1/forgotPassword/${encodePathSegment(payload?.email)}`,
      undefined,
      { timeout: REQUEST_TIMEOUT_MS }
    );

    return {
      data: response.data,
      status: response.status,
      API_Name: "forgotPassword",
    };
  } catch (error) {
    const wrappedError = new Error(error?.response?.data?.message || "Something went wrong");
    wrappedError.status = error?.response?.status;
    throw wrappedError;
  }
};
export const registerUser = async (payload) => {
  try {
    //   const token = await getCsrfToken();
    const response = await axios.post(
      `${API_BASE_URL}/authenticate/register`,
      payload,
      { timeout: REQUEST_TIMEOUT_MS }
    );
    return { response: { data: response.data, status: response.status, statusText: response.statusText }, API_Name: "registerUser" };
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};
export const buyBack = async (payload) => {
  try {
    //   const token = await getCsrfToken();
    const response = await axios.post(
      `${API_BASE_URL}/buyback/v1`,
      payload,
      {
        headers: {
          Authorization: getAuthorizationHeader(),
          ...getPartnerSiteHeaders(),
        },
        timeout: REQUEST_TIMEOUT_MS,
      }
    );
    return { response: { data: response.data, status: response.status, statusText: response.statusText }, API_Name: "buyBack" };
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};
export const currentUser = async () => {
  try {
    //   const token = await getCsrfToken();
    const response = await axios.get(
      `${API_BASE_URL}/customer/v1/current`,
      {
        headers: {
          Authorization: getAuthorizationHeader(),
          ...getPartnerSiteHeaders(),
        },
        timeout: REQUEST_TIMEOUT_MS,
      }
    );
    return { response: { data: response.data, status: response.status, statusText: response.statusText }, API_Name: "currentUser" };
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};
export const updateUser = async (payload) => {
  try {
    //   const token = await getCsrfToken();
    const response = await axios.put(
      `${API_BASE_URL}/customer/v1`,
      payload,
      {
        headers: {
          Authorization: getAuthorizationHeader(),
          ...getPartnerSiteHeaders(),
        },
        timeout: REQUEST_TIMEOUT_MS,
      }
    );
    return { response: { data: response.data, status: response.status, statusText: response.statusText }, API_Name: "updateUser" };
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};
export const view = async () => {
  try {
    //   const token = await getCsrfToken();
    const response = await axios.get(
      `${API_BASE_URL}/dashboard/v1/view`,
      {
        headers: {
          Authorization: getAuthorizationHeader(),
          ...getPartnerSiteHeaders(),
        },
        timeout: REQUEST_TIMEOUT_MS,
      }
    );
    return { response: { data: response.data, status: response.status, statusText: response.statusText }, API_Name: "view" };
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};
export const partnerSiteId = async (payload) => {
  try {
    // Get siteID from payload, URL query params, or sessionStorage
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const cachedData = sessionStorage.getItem("partnerSiteId") &&  sessionStorage.getItem("partnerSiteId") !== "" ? JSON.parse(sessionStorage.getItem("partnerSiteId")) : {};
    
    // Priority: payload > URL > cachedData
    let siteID;
    const normalizedPayloadSiteId = toSafeSiteId(payload);
    if (normalizedPayloadSiteId !== 0) {
      siteID = normalizedPayloadSiteId;
    } else {
      const urlSiteId = urlParams.get("siteID") || urlParams.get("siteid") || urlParams.get("siteId") || urlParams.get("SITEID");
      siteID = toSafeSiteId(urlSiteId) || toSafeSiteId(cachedData?.siteId) || 0;
    }

    // If there's already a pending request for this siteID, return that promise
    if (partnerSiteIdPromise && lastRequestedSiteId === siteID) {
      return await partnerSiteIdPromise;
    }

    // Check if we already have the data in sessionStorage for this siteID
    if (cachedData) {
      try {        
        // If the siteId matches, return cached data without making API call
        if (toSafeSiteId(cachedData?.siteId) === siteID) {
          return {
            response: { data: cachedData, status: 200 },
            API_Name: "partnerSiteId",
            payload: siteID,
            cached: true,
          };
        }
      } catch (e) {
        console.warn("[partnerSiteId] Failed to parse cached data:", e);
      }
    }

    // Create a new promise for this request
    lastRequestedSiteId = siteID;

    partnerSiteIdPromise = axios
      .get(
        `${API_BASE_URL}/partner/v1/site-id/${encodePathSegment(siteID)}`,
        {
          headers: {
            Authorization: getAuthorizationHeader(),
          },
          timeout: REQUEST_TIMEOUT_MS,
        }
      )
      .then((response) => {
        // Cache the response in sessionStorage with the siteID included
        if (response?.data) {
          const dataToCache = {
            ...response.data,
            siteId: siteID,
          };
          sessionStorage.setItem("partnerSiteId", JSON.stringify(dataToCache));
        }

        // Clear the in-flight promise
        partnerSiteIdPromise = null;
        lastRequestedSiteId = null;

        return { response, API_Name: "partnerSiteId", payload: siteID };
      })
      .catch((error) => {
        // Clear the in-flight promise on error
        partnerSiteIdPromise = null;
        lastRequestedSiteId = null;
        throw error;
      });

    return await partnerSiteIdPromise;
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};
export const voucher = async (payload) => {
  try {
    //   const token = await getCsrfToken();
    const response = await axios.get(
      `${API_BASE_URL}/voucher/v1/validate/${encodePathSegment(payload?.values?.voucher)}/product/${encodePathSegment(payload?.selectedItem?.code)}`,
      {
        headers: {
          Authorization: getAuthorizationHeader(),
          ...getPartnerSiteHeaders(),
        },
        timeout: REQUEST_TIMEOUT_MS,
      }
    );
    return { response: { data: response.data, status: response.status, statusText: response.statusText }, API_Name: "voucher", payload: payload };
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};
export const voucherPurchase = async (payload) => {
  try {
    //   const token = await getCsrfToken();
    const response = await axios.post(
      `${API_BASE_URL}/voucher/v1/purchase`,
      payload,
      {
        headers: {
          Authorization: getAuthorizationHeader(),
          ...getPartnerSiteHeaders(),
        },
        timeout: REQUEST_TIMEOUT_MS,
      }
    );
    return { response: { data: response.data, status: response.status, statusText: response.statusText }, API_Name: "voucherPurchase", payload: payload };
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};

export const purchase = async (payload) => {
  try {
    //   const token = await getCsrfToken();
    const response = await axios.post(
      `${API_BASE_URL}/creditCard/v1/purchase`,
      payload,
      {
        headers: {
          Authorization: getAuthorizationHeader(),
          ...getPartnerSiteHeaders(),
        },
        timeout: REQUEST_TIMEOUT_MS,
      }
    );
    return { response: { data: response.data, status: response.status, statusText: response.statusText }, API_Name: "purchase" };
  } catch (error) {
    if (error?.response?.data?.message === "Purchase declined.") {
      error.response.data.message = "Purchase was declined.";
    }
    throw error.response?.data || error.message || error;
  }
};

export const doPaypalPurchase = async (payload) => {
  try {
    // Get fresh CSRF token before making the PayPal purchase call
    // This is critical because the user may have been redirected to PayPal
    // and the previous CSRF token may have expired
    const csrfToken = await getCsrfToken();

    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);

    const token = toSafePayPalHeaderValue(urlParams.get("token"));
    const payerId = toSafePayPalHeaderValue(urlParams.get("PayerID"));

    // Build headers dynamically - only add PP-TOKEN if token exists
    const headers = {
      Authorization: getAuthorizationHeader(),
      ...getPartnerSiteHeaders(),
      "Content-Type": "application/json",
      "X-CSRF-TOKEN": csrfToken, // Explicitly add fresh CSRF token
    };

    if (token) {
      headers["PP-TOKEN"] = token;
    }

    if (payerId) {
      headers["X-PAYERID"] = payerId;
    }

    const response = await axios.post(
      `${API_BASE_URL}/paypal/v1/dopaypalexpresscheckout`,
      payload,
      {
        headers,
        timeout: REQUEST_TIMEOUT_MS,
      }
    );

    return { response: { data: response.data, status: response.status, statusText: response.statusText }, API_Name: "doPaypalPurchase" };
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};
export const paypalAuthorize = async (payload) => {
  try {
    //   const token = await getCsrfToken();
    const response = await axios.post(
      `${API_BASE_URL}/paypal/v1/authorizeexpresscheckout`,
      payload,
      {
        headers: {
          Authorization: getAuthorizationHeader(),
          ...getPartnerSiteHeaders(),
        },
        timeout: REQUEST_TIMEOUT_MS,
      }
    );
    return { response: { data: response.data, status: response.status, statusText: response.statusText }, API_Name: "paypalAuthorize", payload: payload };
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};

export const resetPassword = async (payload) => {
  try {
    const token = encodePathSegment(payload.token);
    const response = await axios.post(
      `${API_BASE_URL}/authenticate/email-password-reset?token=${token}`,
      { email: payload.email, password: payload.password },
      {
        headers: {
          Authorization: getAuthorizationHeader(),
          ...getPartnerSiteHeaders(),
        },
        timeout: REQUEST_TIMEOUT_MS,
      }
    );
    return { response: { data: response.data, status: response.status, statusText: response.statusText }, API_Name: "email-password-reset" };
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};

export const resetLockedAccountPassword = async (payload) => {
  try {
    const token = encodePathSegment(payload.token);
    const response = await axios.post(
      `${API_BASE_URL}/authenticate/email-password-reset?token=${token}&accountunlock=y`,
      { email: payload.email, password: payload.password },
      {
        headers: {
          Authorization: getAuthorizationHeader(),
          ...getPartnerSiteHeaders(),
        },
        timeout: REQUEST_TIMEOUT_MS,
      }
    );
    return { response: { data: response.data, status: response.status, statusText: response.statusText }, API_Name: "resetLockedAccountPassword" };
  } catch (error) {
    throw error.response?.data || error.message || error;
  }
};

